import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Building2, FileText, Target, Shield, BarChart3, AlertCircle, DollarSign, Package } from 'lucide-react';
import NavBar from '../components/NavBar';
import { toast } from 'sonner';
import { AdminDashboardTab } from '../components/admin/AdminDashboardTab';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminAssessmentsTab } from '../components/admin/AdminAssessmentsTab';
import { AdminSalesTab } from '../components/admin/AdminSalesTab';
import { AdminPackagesTab } from '../components/admin/AdminPackagesTab';
import { AdminCompaniesTab } from '../components/admin/AdminCompaniesTab';
import { AdminReportsTab } from '../components/admin/AdminReportsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [users, setUsers] = useState({ users: [], total: 0 });
  const [companies, setCompanies] = useState({ companies: [], total: 0 });
  const [assessments, setAssessments] = useState({ assessments: [], total: 0 });
  const [reports, setReports] = useState({ reports: [], total: 0 });
  const [salesData, setSalesData] = useState(null);
  const [packagePrices, setPackagePrices] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [editingPrices, setEditingPrices] = useState(false);
  const [dateRange, setDateRange] = useState('30');

  const [userPage, setUserPage] = useState(0);
  const [companyPage, setCompanyPage] = useState(0);
  const [assessmentPage, setAssessmentPage] = useState(0);
  const [reportPage, setReportPage] = useState(0);
  const pageSize = 10;

  const [assessmentStatusFilter, setAssessmentStatusFilter] = useState('all');
  const [assessmentTierFilter, setAssessmentTierFilter] = useState('all');

  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '', discount: 10, tier: '', start_date: '', end_date: ''
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchAllData();
  }, [user, navigate]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'companies') fetchCompanies();
    if (activeTab === 'assessments') fetchAssessments();
    if (activeTab === 'reports') fetchReports();
    if (activeTab === 'sales') fetchSalesData();
  }, [activeTab, userPage, companyPage, assessmentPage, reportPage, assessmentStatusFilter, assessmentTierFilter, dateRange]);

  const fetchAllData = async () => {
    try {
      const [statsRes, detailedRes, pricesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/detailed-stats`),
        axios.get(`${API}/admin/package-prices`)
      ]);
      setStats(statsRes.data);
      setDetailedStats(detailedRes.data);
      setPackagePrices(pricesRes.data.prices || {});
      setCampaigns(pricesRes.data.campaigns || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setError(language === 'tr' ? 'Admin erişimi gerekli' : 'Admin access required');
      } else {
        console.error('Error fetching data:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?skip=${userPage * pageSize}&limit=${pageSize}`);
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/admin/companies?skip=${companyPage * pageSize}&limit=${pageSize}`);
      setCompanies(response.data);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchAssessments = async () => {
    try {
      let url = `${API}/admin/assessments?skip=${assessmentPage * pageSize}&limit=${pageSize}`;
      if (assessmentStatusFilter && assessmentStatusFilter !== 'all') url += `&status_filter=${assessmentStatusFilter}`;
      if (assessmentTierFilter && assessmentTierFilter !== 'all') url += `&tier_filter=${assessmentTierFilter}`;
      const response = await axios.get(url);
      setAssessments(response.data);
    } catch (err) {
      console.error('Error fetching assessments:', err);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/admin/reports?skip=${reportPage * pageSize}&limit=${pageSize}`);
      setReports(response.data);
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
  };

  const fetchSalesData = async () => {
    try {
      const response = await axios.get(`${API}/admin/sales?days=${dateRange}`);
      setSalesData(response.data);
    } catch (err) {
      console.error('Error fetching sales:', err);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?new_role=${newRole}`);
      fetchUsers();
      toast.success(language === 'tr' ? 'Rol güncellendi' : 'Role updated');
    } catch (err) {
      toast.error(language === 'tr' ? 'Güncelleme başarısız' : 'Update failed');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(language === 'tr' ? 'Bu kullanıcıyı ve tüm verilerini silmek istediğinizden emin misiniz?' : 'Are you sure you want to delete this user and all their data?')) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      fetchUsers();
      fetchAllData();
      toast.success(language === 'tr' ? 'Kullanıcı silindi' : 'User deleted');
    } catch (err) {
      toast.error(language === 'tr' ? 'Silme başarısız' : 'Delete failed');
    }
  };

  const handleSavePrices = async () => {
    try {
      await axios.put(`${API}/admin/package-prices`, { prices: packagePrices });
      setEditingPrices(false);
      toast.success(language === 'tr' ? 'Fiyatlar güncellendi' : 'Prices updated');
    } catch (err) {
      toast.error(language === 'tr' ? 'Güncelleme başarısız' : 'Update failed');
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignForm.name || !campaignForm.start_date || !campaignForm.end_date) {
      toast.error(language === 'tr' ? 'Lütfen tüm alanları doldurun' : 'Please fill all fields');
      return;
    }
    try {
      await axios.post(`${API}/admin/campaigns`, {
        name: campaignForm.name,
        discount: campaignForm.discount,
        tier: campaignForm.tier && campaignForm.tier !== 'all' ? campaignForm.tier : null,
        start_date: campaignForm.start_date,
        end_date: campaignForm.end_date
      });
      setCampaignForm({ name: '', discount: 10, tier: '', start_date: '', end_date: '' });
      setShowCampaignModal(false);
      fetchAllData();
      toast.success(language === 'tr' ? 'Kampanya oluşturuldu' : 'Campaign created');
    } catch (err) {
      toast.error(language === 'tr' ? 'Kampanya oluşturulamadı' : 'Failed to create campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm(language === 'tr' ? 'Bu kampanyayı silmek istediğinize emin misiniz?' : 'Are you sure you want to delete this campaign?')) return;
    try {
      await axios.delete(`${API}/admin/campaigns/${campaignId}`);
      fetchAllData();
      toast.success(language === 'tr' ? 'Kampanya silindi' : 'Campaign deleted');
    } catch (err) {
      toast.error(language === 'tr' ? 'Silme başarısız' : 'Delete failed');
    }
  };

  const handleToggleCampaign = async (campaignId) => {
    try {
      await axios.put(`${API}/admin/campaigns/${campaignId}/toggle`);
      fetchAllData();
      toast.success(language === 'tr' ? 'Kampanya durumu güncellendi' : 'Campaign status updated');
    } catch (err) {
      toast.error(language === 'tr' ? 'Güncelleme başarısız' : 'Update failed');
    }
  };

  const handleCancelPrices = async () => {
    setEditingPrices(false);
    try {
      const res = await axios.get(`${API}/admin/package-prices`);
      setPackagePrices(res.data.prices || {});
    } catch (err) {
      console.error('Error refetching prices:', err);
    }
  };

  const handlePriceChange = (tier, currency, value) => {
    setPackagePrices((prev) => ({
      ...prev,
      [tier]: { ...prev[tier], [currency]: parseInt(value) || 0 }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <NavBar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-white">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950">
        <NavBar />
        <div className="pt-24 flex items-center justify-center">
          <Card className="bg-red-900/20 border-red-800 max-w-md">
            <CardContent className="py-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-300">{error}</p>
              <Button onClick={() => navigate('/dashboard')} className="mt-4">
                {language === 'tr' ? 'Dashboard\'a Dön' : 'Return to Dashboard'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="admin-page">
      <NavBar />

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-lg bg-purple-500/10 text-purple-400">
              <Shield size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {language === 'tr' ? 'Admin Paneli' : 'Admin Panel'}
              </h1>
              <p className="text-slate-400">
                {language === 'tr' ? 'Platform yönetimi ve istatistikler' : 'Platform management and statistics'}
              </p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800/50 border-slate-700 mb-6 flex flex-wrap w-full justify-start h-auto gap-1">
            <TabsTrigger value="dashboard" data-testid="admin-tab-dashboard" className="data-[state=active]:bg-blue-600">
              <BarChart3 className="h-4 w-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users" className="data-[state=active]:bg-blue-600">
              <Users className="h-4 w-4 mr-2" />
              {language === 'tr' ? 'Üyeler' : 'Users'}
            </TabsTrigger>
            <TabsTrigger value="assessments" data-testid="admin-tab-assessments" className="data-[state=active]:bg-blue-600">
              <Target className="h-4 w-4 mr-2" />
              {language === 'tr' ? 'Değerlendirmeler' : 'Assessments'}
            </TabsTrigger>
            <TabsTrigger value="sales" data-testid="admin-tab-sales" className="data-[state=active]:bg-blue-600">
              <DollarSign className="h-4 w-4 mr-2" />
              {language === 'tr' ? 'Satışlar' : 'Sales'}
            </TabsTrigger>
            <TabsTrigger value="packages" data-testid="admin-tab-packages" className="data-[state=active]:bg-blue-600">
              <Package className="h-4 w-4 mr-2" />
              {language === 'tr' ? 'Paketler' : 'Packages'}
            </TabsTrigger>
            <TabsTrigger value="companies" data-testid="admin-tab-companies" className="data-[state=active]:bg-blue-600">
              <Building2 className="h-4 w-4 mr-2" />
              {language === 'tr' ? 'Şirketler' : 'Companies'}
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="admin-tab-reports" className="data-[state=active]:bg-blue-600">
              <FileText className="h-4 w-4 mr-2" />
              {language === 'tr' ? 'Raporlar' : 'Reports'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <AdminDashboardTab stats={stats} detailedStats={detailedStats} language={language} />
          </TabsContent>

          <TabsContent value="users">
            <AdminUsersTab
              users={users} page={userPage} setPage={setUserPage} pageSize={pageSize}
              onUpdateRole={handleUpdateRole} onDeleteUser={handleDeleteUser} language={language}
            />
          </TabsContent>

          <TabsContent value="assessments">
            <AdminAssessmentsTab
              assessments={assessments} page={assessmentPage} setPage={setAssessmentPage} pageSize={pageSize}
              statusFilter={assessmentStatusFilter} setStatusFilter={setAssessmentStatusFilter}
              tierFilter={assessmentTierFilter} setTierFilter={setAssessmentTierFilter} language={language}
            />
          </TabsContent>

          <TabsContent value="sales">
            <AdminSalesTab salesData={salesData} dateRange={dateRange} setDateRange={setDateRange} language={language} />
          </TabsContent>

          <TabsContent value="packages">
            <AdminPackagesTab
              packagePrices={packagePrices} campaigns={campaigns}
              editingPrices={editingPrices} setEditingPrices={setEditingPrices}
              onSavePrices={handleSavePrices} onCancelPrices={handleCancelPrices} onPriceChange={handlePriceChange}
              showCampaignModal={showCampaignModal} setShowCampaignModal={setShowCampaignModal}
              campaignForm={campaignForm} setCampaignForm={setCampaignForm}
              onCreateCampaign={handleCreateCampaign} onDeleteCampaign={handleDeleteCampaign}
              onToggleCampaign={handleToggleCampaign} language={language}
            />
          </TabsContent>

          <TabsContent value="companies">
            <AdminCompaniesTab companies={companies} page={companyPage} setPage={setCompanyPage} pageSize={pageSize} language={language} />
          </TabsContent>

          <TabsContent value="reports">
            <AdminReportsTab reports={reports} page={reportPage} setPage={setReportPage} pageSize={pageSize} language={language} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPage;
